"use client";

import { useTranslations } from "next-intl";

const TABLE_HEADERS = [
	"coreParamsTableHeaderMaterial",
	"coreParamsTableHeaderPouringTemp",
	"coreParamsTableHeaderTensileStrength",
	"coreParamsTableHeaderHardness",
	"coreParamsTableHeaderDimensionalTolerance",
] as const;

const MATERIAL_ROWS = [
	{
		name: "coreParamsMaterial1Name",
		pouringTemp: "coreParamsMaterial1PouringTemp",
		tensileStrength: "coreParamsMaterial1TensileStrength",
		hardness: "coreParamsMaterial1Hardness",
		tolerance: "coreParamsMaterial1Tolerance",
	},
	{
		name: "coreParamsMaterial2Name",
		pouringTemp: "coreParamsMaterial2PouringTemp",
		tensileStrength: "coreParamsMaterial2TensileStrength",
		hardness: "coreParamsMaterial2Hardness",
		tolerance: "coreParamsMaterial2Tolerance",
	},
	{
		name: "coreParamsMaterial3Name",
		pouringTemp: "coreParamsMaterial3PouringTemp",
		tensileStrength: "coreParamsMaterial3TensileStrength",
		hardness: "coreParamsMaterial3Hardness",
		tolerance: "coreParamsMaterial3Tolerance",
	},
	{
		name: "coreParamsMaterial4Name",
		pouringTemp: "coreParamsMaterial4PouringTemp",
		tensileStrength: "coreParamsMaterial4TensileStrength",
		hardness: "coreParamsMaterial4Hardness",
		tolerance: "coreParamsMaterial4Tolerance",
	},
	{
		name: "coreParamsMaterial5Name",
		pouringTemp: "coreParamsMaterial5PouringTemp",
		tensileStrength: "coreParamsMaterial5TensileStrength",
		hardness: "coreParamsMaterial5Hardness",
		tolerance: "coreParamsMaterial5Tolerance",
	},
] as const;

const GENERAL_ROWS = [
	{ label: "coreParamsGeneralWeightRange", value: "coreParamsGeneralWeightRangeValue" },
	{ label: "coreParamsGeneralMaxMold", value: "coreParamsGeneralMaxMoldValue" },
	{ label: "coreParamsGeneralSurfaceRoughness", value: "coreParamsGeneralSurfaceRoughnessValue" },
	{ label: "coreParamsGeneralDimensionalTolerance", value: "coreParamsGeneralDimensionalToleranceValue" },
	{ label: "coreParamsGeneralTooling", value: "coreParamsGeneralToolingValue" },
	{ label: "coreParamsGeneralSandSystems", value: "coreParamsGeneralSandSystemsValue" },
] as const;

const MELTING_ROWS = [
	{ label: "coreParamsMeltingEquipment", value: "coreParamsMeltingEquipmentValue" },
	{ label: "coreParamsMeltingPouringMethods", value: "coreParamsMeltingPouringMethodsValue" },
	{ label: "coreParamsMeltingSolidification", value: "coreParamsMeltingSolidificationValue" },
	{ label: "coreParamsMeltingHeatTreatment", value: "coreParamsMeltingHeatTreatmentValue" },
	{ label: "coreParamsMeltingQualityStandard", value: "coreParamsMeltingQualityStandardValue" },
	{ label: "coreParamsMeltingProductionCapacity", value: "coreParamsMeltingProductionCapacityValue" },
] as const;

export default function CoreTechnicalParameters() {
	const t = useTranslations("new-sand-casting");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("coreParamsTitle")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("coreParamsDesc")}</p>
				</header>

				<div className="mb-8 overflow-x-auto sm:mb-10 md:mb-12 lg:mb-14">
					<table className="w-full min-w-[800px] border-collapse">
						<thead>
							<tr className="bg-themePrimary400 text-white">
								{TABLE_HEADERS.map((header) => (
									<th
										key={header}
										className="px-4 py-3 text-left text-xs font-bold sm:text-sm md:px-5 md:py-4"
									>
										{t(header)}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{MATERIAL_ROWS.map((row) => (
								<tr key={row.name} className="border-b border-gray-200 bg-white">
									<td className="px-4 py-4 text-xs font-bold text-black sm:text-sm md:px-5 md:py-5">
										{t(row.name)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.pouringTemp)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.tensileStrength)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.hardness)}
									</td>
									<td className="px-4 py-4 text-xs text-black sm:text-sm md:px-5 md:py-5">
										{t(row.tolerance)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>

				<div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 md:gap-6">
					<div className="rounded-md bg-[#f6ffde] p-5 sm:p-6 md:p-7">
						<h3 className="mb-4 text-base font-bold text-themePrimary400 sm:mb-5 sm:text-lg md:mb-6">
							{t("coreParamsGeneralTitle")}
						</h3>
						<dl className="space-y-3 sm:space-y-4">
							{GENERAL_ROWS.map((row) => (
								<div
									key={row.label}
									className="flex items-start justify-between gap-3 border-b border-[#c3c3c1] pb-2 last:border-b-0 last:pb-0"
								>
									<dt className="text-xs text-gray-900 sm:text-sm">{t(row.label)}</dt>
									<dd className="text-right text-xs font-bold text-black sm:text-sm">{t(row.value)}</dd>
								</div>
							))}
						</dl>
					</div>

					<div className="rounded-md bg-[#f6ffde] p-5 sm:p-6 md:p-7">
						<h3 className="mb-4 text-base font-bold text-themePrimary400 sm:mb-5 sm:text-lg md:mb-6">
							{t("coreParamsMeltingTitle")}
						</h3>
						<dl className="space-y-3 sm:space-y-4">
							{MELTING_ROWS.map((row) => (
								<div
									key={row.label}
									className="flex items-start justify-between gap-3 border-b border-[#c3c3c1] pb-2 last:border-b-0 last:pb-0"
								>
									<dt className="text-xs text-gray-900 sm:text-sm">{t(row.label)}</dt>
									<dd className="text-right text-xs font-bold text-black sm:text-sm">{t(row.value)}</dd>
								</div>
							))}
						</dl>
					</div>
				</div>
			</div>
		</section>
	);
}
